import { ReactNode } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
// pbo-core 전역 스타일. 앱 엔트리가 아니라 화면 청크에서 로드해 기존(1.0 iframe·antd 미사용) 화면 영향 범위를 좁힌다
// (SETUP-pbo-core.md §3 — antd 를 쓰지 않던 레포는 화면 래퍼 스코프 적용 권장).
import '@musinsa/pbo-core/tokens.css'
import '@musinsa/pbo-core/styles.css'
import '@musinsa/pbo-core/fonts.css'
import { core } from '@musinsa/pbo-core'
import { pboLogisticsTheme } from '@musinsa/pbo-core/theme'

/**
 * pbo-core 화면 공통 래퍼. 이 안에서만 antd 테마(pboLogisticsTheme)와 Pretendard 서체가 적용된다.
 *
 * 3개 pbo-core 신규 화면(입고조회(전체)·일반입고·일반입고(ERP))이 각자 사본으로 유지하던 것을 승격.
 * 레포 전역(App.tsx)에 두지 않는 이유 — moms-fe 는 antd 미사용 레포라 전역 ConfigProvider 는 기존 화면에 영향을 줄 수 있다.
 *
 * 세로 gap 은 16px 로 컨덕터 emit(`core.Flex vertical gap=16`)에 맞춘다.
 */
export function PboScope({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <core.ConfigProvider theme={pboLogisticsTheme}>
      <PretendardScope />
      <PageContainer className="pbo-core-screen pbo-fonts">{children}</PageContainer>
    </core.ConfigProvider>
  )
}

/** 본문 서체. 전역 GlobalStyles(`* { font-family: notokr }`) 별표 선택자를 이기려면 상속이 아니라 직접 지정이어야 한다. */
const PRETENDARD = "'Pretendard Variable', Pretendard, -apple-system, system-ui, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"

/**
 * antd 포털 루트 목록. 포털은 화면 래퍼 밖 body 에 붙어 화면 스코프가 닿지 않는다.
 *
 * 루트만 지정하면 부족하다 — 루트는 antd 테마 토큰이 Pretendard 를 주지만 그 안쪽 요소는
 * 전역 별표 선택자에 다시 잡힌다. 실측으로 .ant-tooltip 은 Pretendard 인데 .ant-tooltip-inner 는
 * notokr 였다(2026-08-26). 그래서 아래에서 루트와 하위를 짝으로 펼친다.
 */
const ANTD_PORTAL_ROOTS = [
  '.ant-select-dropdown',
  '.ant-picker-dropdown',
  '.ant-modal-root',
  '.ant-popover',
  '.ant-tooltip',
  '.ant-dropdown',
  '.ant-message',
  '.ant-notification',
] as const

const portalSelectors = ANTD_PORTAL_ROOTS.flatMap((root) => [root, `${root} *`]).join(',\n  ')

/**
 * 본문 서체 Pretendard — 컨덕터 "렌더 기본값" 의 시각 계약.
 *
 * antd 컴포넌트는 pbo-core 테마 토큰으로 이미 Pretendard 지만, 필터 라벨처럼 비-antd DOM 은
 * 레포 전역 GlobalStyles 의 별표 선택자(font-family: notokr)가 상속을 끊어 시스템 폰트로 렌더된다
 * (2026-08-26 실측 — .pbo-filter-field 가 notokr 였다). 전역을 바꾸면 1.0 iframe 을 포함한 기존 화면 전부에
 * 영향이 가므로 화면 스코프에서만 재지정한다.
 */
const PretendardScope = createGlobalStyle`
  .pbo-core-screen,
  .pbo-core-screen * {
    font-family: ${PRETENDARD};
  }

  /*
   * 본문 굵기·행간을 DS 기본으로 되돌린다.
   *
   * 레포 전역(GlobalStyles.ts)이 \`html, body { font-weight: 500; line-height: 1.125 }\` 다.
   * 이 둘은 **별표 선택자가 아니라 상속**이라 pbo-core 화면 안까지 흘러들어와, 표 행 글자가 데모보다
   * 한 단계 두껍게(Medium) 보이고 행간도 좁아진다(2026-09-08 사용자 지적 "테이블 행에 텍스트가 좀 두꺼워보이는데").
   * 컨테이너에만 지정한다 — \`*\` 로 강제하면 antd 가 스스로 굵게 잡는 제목·표 헤더·버튼까지 400 으로 눌러 버린다.
   * 값은 antd/DS 기본(fontWeightNormal 400 · 14px 기준 22px)이다.
   */
  .pbo-core-screen {
    font-weight: 400;
    line-height: 1.5714285714285714;
  }

  ${portalSelectors} {
    font-family: ${PRETENDARD};
  }

  /* 포털(모달·드롭다운 등)도 body 밖이라 같은 상속을 받는다 — 루트에만 되돌린다. */
  ${ANTD_PORTAL_ROOTS.join(',\n  ')} {
    font-weight: 400;
    line-height: 1.5714285714285714;
  }
`

/** 표 그룹 안 액션바 ↔ 표 사이 세로 gap(px) — 컨덕터 `core.Flex vertical gap={8}` 기준. */
const TABLE_GROUP_GAP = 8
/** 타이틀 행 표준 높이(40) + 페이지 블록 gap(16) — 타이틀이 고정된 뒤 액션바가 붙는 자리. */
const TITLE_ROW_HEIGHT = 40
const PAGE_BLOCK_GAP = 16
export const ACTION_BAR_STICKY_TOP = TITLE_ROW_HEIGHT + PAGE_BLOCK_GAP
/** 표 액션바 높이(px) — 버튼 32 + 상하 padding 각 4(합 8) = 40. 컨덕터 TableHeader Flex(padding '4px 0') 기준. */
const ACTION_BAR_HEIGHT = 40
/** 표 헤더 stickyOffset — 타이틀(56) + 액션바(40) + gap 8. 세 화면 `core.Table stickyOffset` 이 이 값을 쓴다. */
export const TABLE_STICKY_OFFSET = ACTION_BAR_STICKY_TOP + ACTION_BAR_HEIGHT + TABLE_GROUP_GAP

/*
 * 표 액션바 스티키 CSS 는 pbo-core 0.8.1 부터 DS 가 그린다(pbo-design-system #408 — `.pbo-v2-actionbar-sticky` top 0/z 20/::after 8,
 * 타이틀 스티키(.dev-page-header-wrap.pin) 아래면 top 56 + ::before 16 덮개). 0.8.0 까지 여기 있던 로컬 규칙(24px 덮개·카드 z 4)은
 * DS 규칙보다 명시도가 낮아 어차피 적용되지 않았고(리뷰 지적 2026-09-11), 스크롤 중엔 타이틀의 `.pin.sticky::before` 가 컨테이너 상단
 * 여백(24)을 덮으므로 필요도 없다. 화면은 클래스와 아래 오프셋 상수만 쓴다.
 */

/**
 * 페이지 컨테이너. 블록 사이 세로 gap 16px — 컨덕터 emit(`core.Flex vertical gap={16}`) 기준.
 */
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  /* pbo-core 필터 컨테이너는 min-width 1080px 고정이라 브라우저를 줄여도 필터 영역이 줄지 않는다.
     필드가 min-width 184px + flex-wrap 이라 풀어도 안전하며, pbo-core 자체에 같은 오버라이드 선례가 있다
     (.capmaster-history-filter .dev-search-container { min-width: 0 }). */
  .dev-search-container {
    min-width: 0;
  }
`

export default PboScope
